import { useEffect, useState } from "react";
import { ExternalLink, Save, FileText, Globe } from "lucide-react";

import seoService from "@/services/seoService";

const ROBOTS_URL =
  `${window.location.origin}/robots.txt`;

const SITEMAP_URL =
  `${window.location.origin}/sitemap.xml`;

export default function RobotsSitemapPage() {
  const [robots, setRobots] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  // ==================================================
  // LOAD ROBOTS
  // ==================================================

  useEffect(() => {
    loadRobots();
  }, []);

  async function loadRobots() {
    try {
      setLoading(true);
      setError("");

      const response =
        await seoService.getRobots();

      setRobots(
        response?.data?.content || ""
      );
    } catch (err) {
      console.error(
        "LOAD ROBOTS ERROR:",
        err
      );

      setError(
        err?.response?.data?.message ||
          "Failed to load robots.txt."
      );
    } finally {
      setLoading(false);
    }
  }

  // ==================================================
  // UPDATE ROBOTS
  // ==================================================

  async function handleSave() {
    if (!robots.trim()) {
      setError(
        "Robots.txt content cannot be empty."
      );
      return;
    }

    try {
      setSaving(true);
      setError("");
      setMessage("");

      const response =
        await seoService.updateRobots(
          robots
        );

      setRobots(
        response?.data?.content ||
          robots
      );

      setMessage(
        "robots.txt updated successfully."
      );
    } catch (err) {
      console.error(
        "UPDATE ROBOTS ERROR:",
        err
      );

      setError(
        err?.response?.data?.message ||
          "Failed to update robots.txt."
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* ============================================
          HEADER
      ============================================ */}

      <div className="bg-slate-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div>
            <h1 className="font-heading text-lg font-bold">
              Robots & Sitemap
            </h1>

            <p className="text-xs text-slate-400">
              Manage robots.txt and view sitemap.xml
            </p>
          </div>

          <a
            href={ROBOTS_URL}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-3 py-2 text-sm font-semibold text-slate-200 hover:bg-slate-800"
          >
            <ExternalLink className="h-4 w-4" />
            View robots.txt
          </a>
        </div>
      </div>

      {/* ============================================
          CONTENT
      ============================================ */}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* ==========================================
            PAGE TITLE
        ========================================== */}

        <div className="mb-6">
          <h2 className="font-heading text-2xl font-bold text-slate-900">
            Robots & Sitemap
          </h2>

          <p className="mt-1 text-sm text-slate-500">
            Manage search engine crawling rules and
            view the automatically generated sitemap.
          </p>
        </div>

        {/* ==========================================
            SUCCESS
        ========================================== */}

        {message && (
          <div className="mb-5 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm font-medium text-green-700">
            {message}
          </div>
        )}

        {/* ==========================================
            ERROR
        ========================================== */}

        {error && (
          <div className="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {/* ========================================
              ROBOTS.TXT
          ======================================== */}

          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-900 text-[#F5A623]">
                  <FileText className="h-5 w-5" />
                </div>

                <div>
                  <h3 className="font-heading font-bold text-slate-900">
                    robots.txt
                  </h3>

                  <p className="text-xs text-slate-500">
                    Control search engine crawling
                  </p>
                </div>
              </div>

              <a
                href={ROBOTS_URL}
                target="_blank"
                rel="noreferrer"
                className="text-slate-500 hover:text-slate-900"
                title="Open robots.txt"
              >
                <ExternalLink className="h-5 w-5" />
              </a>
            </div>

            <div className="p-5">
              {loading ? (
                <div className="py-10 text-center text-sm text-slate-400">
                  Loading robots.txt...
                </div>
              ) : (
                <>
                  <textarea
                    value={robots}
                    onChange={(e) => {
                      setRobots(
                        e.target.value
                      );
                      setMessage("");
                      setError("");
                    }}
                    rows={18}
                    spellCheck={false}
                    className="w-full rounded-lg border border-slate-300 bg-slate-950 px-4 py-3 font-mono text-sm leading-6 text-slate-100 outline-none focus:border-[#F5A623] focus:ring-1 focus:ring-[#F5A623]"
                    placeholder="User-agent: *"
                  />

                  <div className="mt-4 flex items-center justify-between gap-3">
                    <a
                      href={ROBOTS_URL}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm font-semibold text-slate-600 hover:text-slate-900"
                    >
                      View live robots.txt
                    </a>

                    <button
                      type="button"
                      onClick={handleSave}
                      disabled={saving || loading}
                      className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Save className="h-4 w-4 text-[#F5A623]" />

                      {saving
                        ? "Updating..."
                        : "Update Robots.txt"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ========================================
              SITEMAP.XML
          ======================================== */}

          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                  <Globe className="h-5 w-5" />
                </div>

                <div>
                  <h3 className="font-heading font-bold text-slate-900">
                    sitemap.xml
                  </h3>

                  <p className="text-xs text-slate-500">
                    Automatically generated sitemap
                  </p>
                </div>
              </div>

              <a
                href={SITEMAP_URL}
                target="_blank"
                rel="noreferrer"
                className="text-slate-500 hover:text-slate-900"
                title="Open sitemap.xml"
              >
                <ExternalLink className="h-5 w-5" />
              </a>
            </div>

            <div className="p-5">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-5">
                <div className="text-sm font-semibold text-slate-700">
                  Sitemap URL
                </div>

                <div className="mt-2 break-all rounded-md bg-white border border-slate-200 px-3 py-2 font-mono text-sm text-slate-600">
                  {SITEMAP_URL}
                </div>

                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="rounded-lg bg-white border border-slate-200 p-4">
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-400">
                      Generation
                    </div>

                    <div className="mt-1 text-sm font-semibold text-slate-900">
                      Automatic
                    </div>
                  </div>

                  <div className="rounded-lg bg-white border border-slate-200 p-4">
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-400">
                      Source
                    </div>

                    <div className="mt-1 text-sm font-semibold text-slate-900">
                      SEO Pages
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-5 rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                Sitemap is generated automatically from
                active public SEO pages. Pages marked
                <strong> noindex </strong>
                are excluded.
              </div>

              <div className="mt-5">
                <a
                  href={SITEMAP_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-bold text-white hover:bg-slate-800"
                >
                  <ExternalLink className="h-4 w-4 text-[#F5A623]" />
                  Open Sitemap.xml
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}